import { Button, Dropdown, Popover, Space } from "antd";
import TextArea, { TextAreaRef } from "antd/es/input/TextArea";
import MagicPencil from "assets/icons/MagicPencil";
import { PremiumFeatureIcon } from "components/GlobalModals/PremiumFeatureModal";
import EmojiPicker, { SkinTones, Theme } from "emoji-picker-react";
import { useEffect, useRef, useState } from "react";
import {
  MdOutlineEmojiEmotions,
  MdRedo,
  MdTextFormat,
  MdUndo,
} from "react-icons/md";
import {
  CAPTION_MODIFY_MENU_ITEMS,
  CaptionModifyMenuKey,
} from "../../postEditorConstants";

type CaptionActionBarProps = {
  caption: string;
  loading: string | boolean;
  canAiRefine: boolean;
  handleCaptionModify: (
    key: CaptionModifyMenuKey,
    instructions?: string
  ) => Promise<void>;
  insertEmoji: (emoji: string) => void;
  theme: "light" | "dark";
  emojiSkinTone: SkinTones;
  setEmojiSkinTone: (skinTone: SkinTones) => void;
  handleUndo: () => void;
  handleRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
};

const CaptionActionBar = ({
  caption,
  loading,
  canAiRefine,
  handleCaptionModify,
  insertEmoji,
  theme,
  emojiSkinTone,
  setEmojiSkinTone,
  handleUndo,
  handleRedo,
  canUndo,
  canRedo,
}: CaptionActionBarProps) => {
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [customInstructions, setCustomInstructions] = useState("");
  const textAreaRef = useRef<TextAreaRef>(null);

  // Focus the TextArea when the popover opens
  useEffect(() => {
    if (popoverVisible) {
      // Add a small delay to ensure the element is rendered and visible
      const timer = setTimeout(() => {
        textAreaRef.current?.focus();
      }, 100); // 100ms delay
      return () => clearTimeout(timer); // Cleanup the timer
    }
  }, [popoverVisible]);

  const handleApplyInstructions = (instruction: string) => {
    handleCaptionModify("custom", instruction);
    setPopoverVisible(false);
    setCustomInstructions(""); // Clear instructions after applying
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter key press without Shift key
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault(); // Prevent default Enter behavior (newline)
      // Only submit if there are instructions
      if (customInstructions.trim()) {
        handleApplyInstructions(customInstructions);
      }
    }
    // Note: Shift + Enter will now behave as default (insert newline)
    // because we are not preventing its default behavior.
  };

  const handleSubmit = () => {
    handleApplyInstructions(customInstructions);
  };

  return (
    <div className="flex justify-between items-center mt-1">
      <div className="flex gap-1 items-center">
        {/* Keep existing buttons for when caption is not empty */}
        {caption.length > 0 && (
          <>
            {/* Improve Button with Popover */}
            <Popover
              content={
                <Space direction="vertical" className="w-full">
                  <TextArea
                    ref={textAreaRef}
                    placeholder="What would you like to improve?"
                    value={customInstructions}
                    onChange={({ target }) =>
                      setCustomInstructions(target.value)
                    }
                    onKeyDown={handleKeyDown}
                    autoSize={{ minRows: 1, maxRows: 6 }}
                    // Focus is typically handled by the Popover's trigger
                  />
                  <Button
                    type="primary"
                    size="small"
                    onClick={handleSubmit}
                    // Disable button if instructions are empty
                    disabled={!customInstructions.trim()}
                  >
                    Improve
                  </Button>
                </Space>
              }
              title="Improve Caption"
              trigger="click"
              open={popoverVisible}
              onOpenChange={setPopoverVisible}
              placement="bottomLeft"
              overlayClassName="w-72"
            >
              <Button
                loading={loading === "caption"}
                size="small"
                type="text"
                icon={canAiRefine ? <MagicPencil /> : <PremiumFeatureIcon />}
              >
                <span className="sm:!inline !hidden">Improve</span>
              </Button>
            </Popover>

            {/* Length Dropdown Button */}
            <Dropdown
              menu={{
                items: CAPTION_MODIFY_MENU_ITEMS.filter(
                  (item) => item?.key === "shorten" || item?.key === "lengthen"
                ),
                onClick: ({ key }) =>
                  handleCaptionModify(key as CaptionModifyMenuKey),
              }}
            >
              <Button
                loading={loading === "caption"}
                size="small"
                type="text"
                icon={canAiRefine ? <MdTextFormat /> : <PremiumFeatureIcon />}
              >
                <span className="sm:!inline !hidden">Length</span>
              </Button>
            </Dropdown>
          </>
        )}

        <Popover
          className="[&_.ant-popover-inner]:!p-0"
          overlayClassName="[&_.ant-popover-inner]:!p-0"
          placement="bottomRight"
          showArrow={false}
          arrow={false}
          content={
            <EmojiPicker
              height={248}
              onEmojiClick={({ emoji }) => insertEmoji(emoji)}
              theme={theme === "dark" ? Theme.DARK : Theme.AUTO}
              defaultSkinTone={emojiSkinTone}
              onSkinToneChange={setEmojiSkinTone}
              previewConfig={{ showPreview: false }}
            />
          }
        >
          <Button size="small" type="text" icon={<MdOutlineEmojiEmotions />} />
        </Popover>
      </div>
      <div className="flex items-center gap-1">
        {/* Undo Button */}
        <Button
          size="small"
          type="text"
          icon={<MdUndo />}
          onClick={handleUndo}
          disabled={!canUndo}
        />
        {/* Redo Button */}
        <Button
          size="small"
          type="text"
          icon={<MdRedo />}
          onClick={handleRedo}
          disabled={!canRedo}
        />
        {/* Character Count */}
        <div className="text-antd-colorTextSecondary w-8 text-right">
          {caption.length}
        </div>
      </div>
    </div>
  );
};

export default CaptionActionBar;
